// AIDEV-NOTE: IFS type-family classification for part-similarity candidate filtering.
// A new interaction is only compared against existing parts in the SAME family (plus
// unknown-typed parts, which stay matchable everywhere). This is the "filtered-compact"
// config (2026-06-12): family filtering raised top-2 recall to 85% vs 77% baseline on
// 26 production GT cases while cutting output tokens ~10x. Known trade-off: ~4% of
// genuine cross-family merges (e.g. an Exile later re-typed as a Protector) are excluded
// by design — see the moved cross-family fixture case.

export type PartTypeFamily =
    | "protector"
    | "exile"
    | "unattached-burden"
    | "guide"
    | "unknown";

/**
 * Classify a free-text `part.type` value into a coarse IFS type family.
 * Combined / ambiguous types (and Self) resolve to "unknown" so they stay
 * matchable across every family.
 */
export function getPartTypeFamily(
    type: string | null | undefined,
): PartTypeFamily {
    if (!type) return "unknown";
    const t = type.toLowerCase();
    const isProtector =
        t.includes("protector") ||
        t.includes("manager") ||
        t.includes("firefighter") ||
        t.includes("self-like");
    const isExile = t.includes("exile");
    if (isProtector && isExile) return "unknown"; // combined types stay matchable everywhere
    if (t.includes("unattached")) return "unattached-burden";
    if (isProtector) return "protector";
    if (isExile) return "exile";
    if (t.includes("guide")) return "guide";
    return "unknown";
}

/**
 * Filter candidate parts to those in the same type family as `newPartType`
 * (plus unknown-typed candidates). When the new part's family is "unknown" we
 * cannot safely narrow, so all candidates are returned.
 */
export function filterPartsByTypeFamily<T extends { type?: string | null }>(
    candidates: T[],
    newPartType: string | null | undefined,
): T[] {
    const family = getPartTypeFamily(newPartType);
    if (family === "unknown") return candidates;
    return candidates.filter((c) => {
        const cf = getPartTypeFamily(c.type);
        return cf === family || cf === "unknown";
    });
}
